import React from "react";
import {
  Badge,
  Box,
  Button,
  DialogTitle,
  Icon,
  Text,
  type IconName,
} from "@salestrak/ui";

import { useTeamDetail } from "#app/lib/hooks/use-team-detail";
import { useAppData, type TeamRosterUser } from "#app/lib/data";
import { usePermission } from "#app/lib/hooks/use-permission";
import { userRefToEntity } from "#app/lib/entity-visuals";

import { EntityTag } from "./entity-tag";
import { EntityGroupTag } from "./entity-group";
import { DetailFieldRow } from "./detail-field-row";
import { EntityDetailShell, EntityDetailCard } from "./entity-detail-shell";
import type { Omittable } from "@salestrak/ui";

type TeamProfileDialogProps = Omittable<{
  teamId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Rename / team settings — caller gates this on `manage.teams`.
  onEditTeam?: (teamId: string) => void;
  // Membership management for a LEAD of this team who lacks `manage.teams`
  // (admins reach the same screen through "Edit team" → settings). Always
  // wired by the caller; the lead/permission gating lives here.
  onManageMembers?: (teamId: string) => void;
}>;

function PeopleRow({
  icon,
  label,
  users,
}: {
  icon: IconName;
  label: string;
  users: TeamRosterUser[];
}) {
  if (users.length === 0) {
    return <DetailFieldRow icon={icon} label={label} value={null} />;
  }

  return (
    <DetailFieldRow icon={icon} label={label} align="start">
      <EntityGroupTag
        entities={users.map(userRefToEntity)}
        variant="stack"
        max={5}
        size="sm"
        title={label}
      />
    </DetailFieldRow>
  );
}

export function TeamProfileDialog({
  teamId,
  open,
  onOpenChange,
  onEditTeam,
  onManageMembers,
}: TeamProfileDialogProps) {
  const { team, glyph } = useTeamDetail(teamId ?? undefined, { skip: !open });
  const { currentUser } = useAppData();
  const canManageTeams = usePermission("manage.teams");

  // A lead of THIS team (who isn't already a manage.teams admin) gets a
  // direct "Manage teammates" affordance — admins reach the same screen
  // through "Edit team" → team settings, so we don't double up for them.
  const isLead = team?.viewerRole === "lead";
  const showManageMembers =
    !!onManageMembers && !!teamId && isLead && !canManageTeams;

  const leads = team?.leads ?? [];
  const members = team?.members ?? [];
  const teamName = team?.teamName ?? "Loading...";

  const header = (
    <Box direction="row" align="center" gap="md">
      <EntityTag
        entity={{
          kind: "team",
          id: teamId ?? "team",
          team: {
            teamName: team?.teamName ?? "",
            teamKey: team?.teamKey,
            icon: { icon: glyph.icon, color: glyph.color },
          },
        }}
        display="visual"
        size="lg"
        interactive={false}
      />
      <Box gap="xs">
        <DialogTitle>{teamName}</DialogTitle>
        <Box direction="row" align="center" gap="sm">
          <Badge size="sm" variant={team?.private ? "warning" : "secondary"}>
            {team?.private ? "Private" : "Public"}
          </Badge>
          {team?.teamKey ? (
            <Text size="sm" variant="muted">{team.teamKey}</Text>
          ) : null}
        </Box>
      </Box>
    </Box>
  );

  const factsCard = (
    <EntityDetailCard>
      <Box direction="column" gap="lg">
        <PeopleRow icon="shield" label="Leads" users={leads} />
        <PeopleRow icon="users" label="Teammates" users={members} />
      </Box>
    </EntityDetailCard>
  );

  const canEdit = onEditTeam && teamId;

  // Edit-team (admins) and Manage-teammates (leads) are mutually exclusive in
  // practice. The shell's `onEdit` supersedes `footer`, so admins get the
  // edit action and leads fall through to this footer button.
  const manageMembersFooter = showManageMembers ? (
    <Button
      className="w-full"
      onClick={() => {
        onManageMembers!(teamId!);
        onOpenChange(false);
      }}
    >
      <Icon name="users" />
      Manage teammates
    </Button>
  ) : undefined;

  return (
    <EntityDetailShell
      open={open}
      onOpenChange={onOpenChange}
      header={header}
      srDescription={`${team?.teamName ?? "Team"} profile`}
      body={factsCard}
      footer={manageMembersFooter}
      onEdit={canEdit ? () => onEditTeam!(teamId!) : undefined}
      editLabel="Edit team"
    />
  );
}
